import { useEffect, useRef, useState } from "react";

import {
  MIN_SETTLEMENT_QUERY_LENGTH,
  searchGeoSettlements,
} from "../api/geo";
import { geoMessageFromError } from "../lib/geoErrors";
import {
  GEO_SEARCH_TIMEOUT_MESSAGE,
  GEO_SEARCH_TIMEOUT_MS,
  isGeoSearchAbortError,
  isGeoSearchTimeoutError,
} from "../lib/geoSearchTimeout";
import { settlementToComboboxOption } from "../lib/locationGeo";

export const SETTLEMENT_DEBOUNCE_MS = 400;

const searchCache = new Map();

function cacheKey(regionCode, query, page) {
  return `${regionCode}|${query.trim().toLowerCase()}|${page}`;
}

// Settlement autocomplete search with debounce, AbortController cancellation,
// request-sequence stale protection, and a small in-memory session cache.

export function useGeoSettlementSearch({ regionCode, query, enabled }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const latestRequestIdRef = useRef(0);
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const timeoutWatchIdRef = useRef(null);

  useEffect(() => {
    const trimmed = String(query ?? "").trim();

    if (!enabled || !regionCode) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (timeoutWatchIdRef.current) {
        clearTimeout(timeoutWatchIdRef.current);
        timeoutWatchIdRef.current = null;
      }
      latestRequestIdRef.current += 1;
      setOptions([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    if (trimmed.length < MIN_SETTLEMENT_QUERY_LENGTH) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (timeoutWatchIdRef.current) {
        clearTimeout(timeoutWatchIdRef.current);
        timeoutWatchIdRef.current = null;
      }
      latestRequestIdRef.current += 1;
      setOptions([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    const key = cacheKey(regionCode, trimmed, 1);
    if (searchCache.has(key)) {
      setOptions(searchCache.get(key));
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (timeoutWatchIdRef.current) {
      clearTimeout(timeoutWatchIdRef.current);
      timeoutWatchIdRef.current = null;
    }

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      const controller = new AbortController();
      abortControllerRef.current = controller;
      if (timeoutWatchIdRef.current) {
        clearTimeout(timeoutWatchIdRef.current);
        timeoutWatchIdRef.current = null;
      }
      timeoutWatchIdRef.current = setTimeout(() => {
        timeoutWatchIdRef.current = null;
        if (latestRequestIdRef.current !== requestId) return;
        controller.abort();
        setError(GEO_SEARCH_TIMEOUT_MESSAGE);
        setOptions([]);
        setLoading(false);
      }, GEO_SEARCH_TIMEOUT_MS);

      searchGeoSettlements({
        query: trimmed,
        regionCode,
        page: 1,
        signal: controller.signal,
      })
        .then((data) => {
          if (timeoutWatchIdRef.current) {
            clearTimeout(timeoutWatchIdRef.current);
            timeoutWatchIdRef.current = null;
          }
          if (latestRequestIdRef.current !== requestId) return;
          const results = Array.isArray(data?.results) ? data.results : [];
          const mapped = results.map(settlementToComboboxOption);
          searchCache.set(key, mapped);
          setOptions(mapped);
          setLoading(false);
        })
        .catch((err) => {
          if (timeoutWatchIdRef.current) {
            clearTimeout(timeoutWatchIdRef.current);
            timeoutWatchIdRef.current = null;
          }
          if (latestRequestIdRef.current !== requestId) return;
          if (isGeoSearchTimeoutError(err)) {
            setError(GEO_SEARCH_TIMEOUT_MESSAGE);
            setOptions([]);
            setLoading(false);
            return;
          }
          if (controller.signal.aborted || isGeoSearchAbortError(err)) {
            return;
          }
          setError(geoMessageFromError(err));
          setOptions([]);
          setLoading(false);
        });
    }, SETTLEMENT_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [enabled, regionCode, query]);

  useEffect(() => {
    return () => {
      latestRequestIdRef.current += 1;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (timeoutWatchIdRef.current) {
        clearTimeout(timeoutWatchIdRef.current);
        timeoutWatchIdRef.current = null;
      }
    };
  }, []);

  return { options, loading, error };
}

export function __resetGeoSettlementCacheForTests() {
  searchCache.clear();
}
